import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';
import {
  Essay,
  CreateEssayData,
  UpdateEssayData,
  GetUserEssaysParams,
  EssaysResponse,
  SingleEssayResponse
} from '../types/Essay';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export class EssayService {
  /**
   * Create a new essay
   */
  static async createEssay(essayData: CreateEssayData): Promise<Essay> {
    const response = await makeAuthenticatedRequest('/essays', {
      method: 'POST',
      body: JSON.stringify(essayData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create essay');
    }

    return response.data;
  }

  /**
   * Get an essay by essay_id (requires user_id for security)
   */
  static async getEssayById(essayId: string, userId: number): Promise<Essay> {
    const response = await makeAuthenticatedRequest(`/essays/${essayId}?user_id=${userId}`);

    if (!response.success) {
      throw new Error('Failed to fetch essay');
    }

    return response.data;
  }

  /**
   * Get user's essays with optional filtering and pagination
   */
  static async getUserEssays(userId: number, params: GetUserEssaysParams = {}): Promise<EssaysResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const url = `/essays/user/${userId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch user essays');
    }

    return response;
  }

  /**
   * Update an essay
   */
  static async updateEssay(essayId: string, updateData: UpdateEssayData): Promise<Essay> {
    const response = await makeAuthenticatedRequest(`/essays/${essayId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update essay');
    }

    return response.data;
  }

  /**
   * Delete an essay
   */
  static async deleteEssay(essayId: string): Promise<void> {
    const response = await makeAuthenticatedRequest(`/essays/${essayId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete essay');
    }
  }

  /**
   * Create an essay with auto-generated ID from title
   */
  static async createEssayFromTitle(userId: number, title: string, content?: string): Promise<Essay> {
    return await this.createEssay({
      user_id: userId,
      title,
      content
    });
  }

  /**
   * Get recent essays for a user
   */
  static async getRecentEssays(userId: number, limit: number = 10): Promise<Essay[]> {
    const response = await this.getUserEssays(userId, {
      limit,
      sort_order: 'DESC'
    });

    return response.data;
  }

  /**
   * Get on-going essays for a user
   */
  static async getOnGoingEssays(userId: number): Promise<Essay[]> {
    const response = await this.getUserEssays(userId, {
      limit: 100,
      sort_order: 'DESC'
    });

    // Filter for on-going status
    return response.data.filter(essay => essay.status === 'on-going');
  }
}

export default EssayService;

